// The Edits tab's working copy of an edit (#401).
//
// Before this card set existed, "what am I applying" was split across three
// places: the motion tuning panel, the render dialog's transition presets and
// its deflicker/watermark switches. The dialog owned its own React state, so
// the answer only existed while the dialog was open. The operation cards need
// that answer on the page, so the state moves up to ProjectView and this module
// is the pure half of it: derive the draft from persisted settings, decide
// whether it still matches them, and project it back onto the settings blob the
// worker reads.
//
// Every function here is pure and takes plain data — the cards, the render
// dialog receipt and the persist path in handleConfirmRender all go through the
// same three helpers, so the switch on screen, the duration in the dialog and
// the bytes written to Firestore cannot disagree.

import {
  OPERATIONS_BY_UI_ORDER,
  getOperation,
  requestedEnabled,
  buildOperationsBlock,
  resolveOperations,
  OPERATIONS_KEY,
  TRIM_STILLNESS,
  VARIABLE_SPEED,
  DEFLICKER,
  WATERMARK_REMOVAL,
} from './operations';
import { normalizeDeflickerWindow } from './flickerPreview';
import { normalizeGapFrames } from './captureGaps';
import {
  resolveMaxSpeed,
  resolveSmoothness,
  paceOutputSpan,
  remapGapFramesToOutput,
  SPEED_CURVE_FIELD,
} from './motionPace';

export { OPERATIONS_BY_UI_ORDER };

// Absent transitions read as 0 frames (a hard cut), matching what the render
// dialog has always shown for a project that has never been rendered. The
// registry's `absentDefault: 15` describes what the PRE-#400 worker did with a
// missing key; showing "Quick fade" for a setting the user never chose would be
// the UI lying about what it is going to render.
export const DEFAULT_TRANSITION_FRAMES = 0;

// --- watermark customization ------------------------------------------------
//
// Paid accounts can swap the Tweedia mark for an uploaded logo and tune its
// corner/size/opacity. Every constant here mirrors the worker's clamps in
// packages/containers/generatevideo/processing/watermark.py and the API's
// sanitizer in timelapse/api/main.py — the client's copy of a contract each
// side re-enforces for itself.

export const WATERMARK_POSITIONS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
export const WATERMARK_POSITION_DEFAULT = 'bottom-right';
export const WATERMARK_SIZE_MIN = 0.05;
export const WATERMARK_SIZE_MAX = 0.30;
export const WATERMARK_SIZE_DEFAULT = 0.14;
export const WATERMARK_OPACITY_MIN = 0.05;
export const WATERMARK_OPACITY_MAX = 1.0;
export const WATERMARK_OPACITY_DEFAULT = 0.7;
// The stock Tweedia chip composites at FULL strength: its restraint is painted
// into the asset's own alpha, so the worker does not fade it a second time.
// Mirrors STOCK_OPACITY in processing/watermark.py — a Free account's preview
// has to show the mark at the strength that actually lands in the mp4.
export const WATERMARK_STOCK_OPACITY = 1.0;
export const WATERMARK_LOGO_MAX_BYTES = 5 * 1024 * 1024;

// A LOGO ID IS A SLOT NAME, NOT A FILE NAME. storage.rules only lets a client
// create `watermarks/{uid}/logo0`..`logo4` — five fixed slots, which is what
// bounds that un-metered prefix — and both server sanitizers
// (timelapse/api/main.py's _WATERMARK_LOGO_ID_RE, the worker's _LOGO_ID_RE)
// refuse anything else. An upload named after its file would be rejected by
// the rules; an id named after one would be dropped by the API and silently
// render the Tweedia mark instead. The human label for the picker rides in the
// object's `customMetadata.displayName` — see `watermarkLogoLabel` below.
export const WATERMARK_LOGO_SLOTS = ['logo0', 'logo1', 'logo2', 'logo3', 'logo4'];
const WATERMARK_LOGO_ID_RE = /^logo[0-4]$/;

export const isWatermarkLogoId = (value) =>
  typeof value === 'string' && WATERMARK_LOGO_ID_RE.test(value);

// The lowest slot nothing occupies, or null when all five are taken (the
// upload tile turns itself off and says so). Lowest-first rather than
// append-at-the-end so deleting a logo actually frees the slot it held.
export const firstFreeWatermarkSlot = (usedIds) => {
  const used = new Set(Array.isArray(usedIds) ? usedIds : []);
  return WATERMARK_LOGO_SLOTS.find((slot) => !used.has(slot)) || null;
};

// The fallback label for a slot whose object carries no `displayName` —
// uploaded before the metadata existed, or written by something other than
// this card. 1-based because "logo0" is a wire detail, not a thing to show.
export const watermarkSlotLabel = (logoId) => {
  const index = WATERMARK_LOGO_SLOTS.indexOf(logoId);
  return index < 0 ? 'Logo' : `Logo ${index + 1}`;
};

// Longest human label stored in `customMetadata.displayName`. Storage caps
// custom metadata far above this; the ceiling is about the tile, which has one
// line to render it in.
export const WATERMARK_LOGO_LABEL_MAX = 64;

// A picked file's name reduced to a label worth showing: path stripped,
// control characters out, whitespace collapsed, capped. This is DISPLAY ONLY —
// it never becomes an id or a path, so it needs no character allowlist. Empty
// input yields '' and the caller falls back to the slot label.
export const watermarkLogoLabel = (name) => {
  const base = String(name == null ? '' : name).split(/[\\/]/).pop() || '';
  // eslint-disable-next-line no-control-regex
  return base.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim()
    .slice(0, WATERMARK_LOGO_LABEL_MAX);
};

// A number (or numeric string — Firestore docs are client-writable, so a
// corrupt doc can hold anything) clamped into [lo, hi]; null when the value is
// not a number at all, so hydration can fall through to the next source.
const coerceClamped = (value, lo, hi) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(hi, Math.max(lo, n));
};

export const normalizeWatermarkPosition = (value) =>
  (WATERMARK_POSITIONS.includes(value) ? value : WATERMARK_POSITION_DEFAULT);

export const normalizeWatermarkSize = (value) => {
  const clamped = coerceClamped(value, WATERMARK_SIZE_MIN, WATERMARK_SIZE_MAX);
  return clamped === null ? WATERMARK_SIZE_DEFAULT : clamped;
};

// WHICH DEFAULT APPLIES DEPENDS ON WHICH ASSET RENDERS — the client's mirror of
// default_opacity() in processing/watermark.py. The stock chip carries its
// restraint in its own alpha and composites at full strength; an uploaded logo
// is arbitrary opaque artwork that still needs toning down. An absent (or
// unusable) id names no upload, so it resolves to the chip — the same way
// build_spec's `asset_path` defaults to the stock ASSET_PATH.
export const defaultWatermarkOpacity = (logoId) =>
  (isWatermarkLogoId(logoId) ? WATERMARK_OPACITY_DEFAULT : WATERMARK_STOCK_OPACITY);

// `logoId` names the asset the value is FOR, and is only consulted when there is
// no usable value to clamp.
export const normalizeWatermarkOpacity = (value, logoId) => {
  const clamped = coerceClamped(value, WATERMARK_OPACITY_MIN, WATERMARK_OPACITY_MAX);
  return clamped === null ? defaultWatermarkOpacity(logoId) : clamped;
};

// The four watermark fields for one draft, resolved through the hydration
// precedence: this edit's saved settings -> the account's last-used defaults
// (users/{uid}.watermarkDefaults, written by the API on every marked render) ->
// the worker's built-ins. Each field falls through INDEPENDENTLY, and every
// candidate is validated on the way in so a corrupt doc can't wedge the card.
//
// `watermarkLogoId: null` in settings is an explicit choice (the Tweedia tile),
// so presence — not truthiness — decides whether it outranks the account
// default.
//
// OPACITY IS THE ONE FIELD THAT MAY STAY UNRESOLVED. When neither source names
// one, it is left null — "no opinion" — rather than filled in with a number,
// because the applicable default depends on which asset ends up rendering and
// the tile can still change. Every consumer resolves it through
// `defaultWatermarkOpacity`, so the slider, the preview and the render request
// all land on the same value. This mirrors customization_from_settings(), which
// likewise leaves an omitted opacity omitted.
const hydrateWatermarkFields = (s, accountDefaults) => {
  const a = accountDefaults && typeof accountDefaults === 'object' && !Array.isArray(accountDefaults)
    ? accountDefaults
    : {};
  const logoFromSettings = 'watermarkLogoId' in s
    && (s.watermarkLogoId === null || isWatermarkLogoId(s.watermarkLogoId));
  const size = coerceClamped(s.watermarkSize, WATERMARK_SIZE_MIN, WATERMARK_SIZE_MAX);
  const opacity = coerceClamped(s.watermarkOpacity, WATERMARK_OPACITY_MIN, WATERMARK_OPACITY_MAX);
  return {
    watermarkLogoId: logoFromSettings
      ? s.watermarkLogoId
      : (isWatermarkLogoId(a.logoId) ? a.logoId : null),
    watermarkPosition: WATERMARK_POSITIONS.includes(s.watermarkPosition)
      ? s.watermarkPosition
      : normalizeWatermarkPosition(a.position),
    watermarkSize: size === null ? normalizeWatermarkSize(a.size) : size,
    watermarkOpacity: opacity === null
      ? coerceClamped(a.opacity, WATERMARK_OPACITY_MIN, WATERMARK_OPACITY_MAX)
      : opacity,
    // DISPLAY ONLY, and deliberately not hydrated: the human label lives in
    // the Storage object's `customMetadata.displayName`, which only the logo
    // library reads. It joins the draft when a tile is picked or uploaded and
    // is stripped from the render request below, so a draft rebuilt from
    // Firestore alone starts blank and the receipt says "Your logo".
    watermarkLogoName: null,
  };
};

// The watermark half of the generate_edited_video payload. All four
// customization keys are ALWAYS present: keys absent from the request leave
// the saved settings untouched server-side, and an explicit
// `watermarkLogoId: null` is how re-selecting Tweedia clears a saved custom
// logo — omitting it would resurrect the old one. Normalised once more here
// because these are the values the render runs.
//
// Built from an explicit key list, so the draft's display-only
// `watermarkLogoName` never reaches the wire.
//
// An unresolved opacity is settled HERE, against the logo id this request
// actually carries — not the raw draft's. Every value on the wire is explicit,
// so a request that named 0.7 for an untouched slider would read to the worker
// as a deliberate choice and fade the stock chip a second time, which is the
// washed-out-on-bright-footage bug this asset was redrawn to fix.
export const watermarkRenderRequest = (draft) => {
  const d = draft || {};
  const logoId = isWatermarkLogoId(d.watermarkLogoId) ? d.watermarkLogoId : null;
  return {
    removeWatermark: !!d.removeWatermark,
    watermarkLogoId: logoId,
    watermarkPosition: normalizeWatermarkPosition(d.watermarkPosition),
    watermarkSize: normalizeWatermarkSize(d.watermarkSize),
    watermarkOpacity: normalizeWatermarkOpacity(d.watermarkOpacity, logoId),
  };
};

// The card state for one project, derived from its persisted settings.
//
// `trimStillness` and `deflicker` go through requestedEnabled, so an explicit
// `settings.operations.<id>.enabled` wins over the legacy tuning key and a
// project that has never carried the enable layer still resolves to today's
// behavior. `removeWatermark` is a PREFERENCE, not the effective value: the
// account's entitlement is applied where the switch is rendered, and the worker
// re-applies it authoritatively at render time.
//
// `watermarkDefaults` is the optional account-level fallback for the four
// watermark customization fields (users/{uid}.watermarkDefaults) — consulted
// only for fields this edit's settings don't claim.
//
// PRESENCE IS NOT PERMISSION. The API only writes that blob for accounts that
// hold `watermarkRemoval`, but entitlement can lapse afterwards (a Pro account
// that downgrades keeps its saved look), so the CALLER must pass null for an
// account that no longer holds it — see ProjectView. Otherwise a Free account
// would open on a custom logo the render will never honour.
export const deriveEditDraft = (settings, watermarkDefaults) => {
  const s = settings || {};
  return {
    ...hydrateWatermarkFields(s, watermarkDefaults),
    trimStillness: requestedEnabled(getOperation(TRIM_STILLNESS), s),
    // MotionPace (#528). A REQUEST, like `removeWatermark` is: the operation is
    // dark-launched behind the `motionPace` feature flag, and the flag is
    // applied where the control is rendered (and again, authoritatively, in the
    // worker). Deriving it through requestedEnabled means an explicit
    // `settings.operations.variable_speed.enabled` outranks the legacy
    // `variableSpeed` key here exactly as it does everywhere else.
    variableSpeed: requestedEnabled(getOperation(VARIABLE_SPEED), s),
    // MotionPace's two tuning values. CLAMPED, NEVER ROUNDED (#454): a stored
    // 4.237 survives a round trip through this draft bit-identical, so opening
    // the card and closing it cannot quantize a value an API client chose.
    maxSpeed: resolveMaxSpeed(s.maxSpeed),
    smoothness: resolveSmoothness(s.smoothness),
    // MotionPace's burned-in speed dial (#533). A PRESENTATION option on the
    // operation, not an operation of its own, so it has no enable-layer entry
    // and is read straight off the blob like `captureGapTransitions`.
    //
    // `=== true`, NOT truthiness. The worker gates the burn-in on
    // `settings.get('showSpeedDial') is True` (video_editing.py) because
    // `validate_settings` copies allowlisted values through verbatim — so a
    // hand-written or API-written `"false"` renders NO dial there, and a truthy
    // read here would draw one in the preview. Same strictness, both sides.
    showSpeedDial: s.showSpeedDial === true,
    deflicker: requestedEnabled(getOperation(DEFLICKER), s),
    transitionFrames: Number.isFinite(s.transition_length_frames)
      ? s.transition_length_frames
      : DEFAULT_TRANSITION_FRAMES,
    removeWatermark: requestedEnabled(getOperation(WATERMARK_REMOVAL), s),
    // Not a registry operation (no `enable` descriptor) — it is a plain bool
    // the worker reads straight off `settings.captureGapTransitions`, so a
    // simple truthy read is the whole derivation. Defaults off/absent.
    captureGapTransitions: !!s.captureGapTransitions,
    // The sibling declaration for combined videos: fade at EVERY clip join
    // (`video.concatJoinFrames`), not only at the joins the concat worker could
    // prove were a long camera-off stretch. Same shape as the key above — not a
    // registry operation, a plain bool the worker reads off
    // `settings.clipJoinTransitions`.
    //
    // ABSENT MEANS OFF HERE, deliberately. The "fade at every join" default for
    // a combined project that has never rendered lives in `seedEditDraft`
    // (`defaultsToJoinFades`), not in this function. A default applied here
    // would move the draft and the PERSISTED BASELINE together — this is what
    // `editDraftDiffersFromPersisted` re-derives — so the comparison would
    // report no change, `handleConfirmRender` would skip the settings write,
    // and the worker (`settings.get("clipJoinTransitions") is True`,
    // video_editing.py) would read the key as absent and hard-cut underneath a
    // UI showing a ticked box. Same trap the `trim_stillness` note below warns
    // about.
    clipJoinTransitions: !!s.clipJoinTransitions,
    // "Fix flicker" Strength, in source frames — a tuning value, not an enable
    // state, so it is read straight off the blob like `captureGapTransitions`.
    // Snapped to a supported choice HERE rather than only where the tiles are
    // drawn, so the tile selection, the persist comparison below and the proof
    // graph all read one window. The snap matches the worker's
    // (video_editing.py::_coerce_deflicker_window), which is what makes a
    // hand-written 12 both SHOW and RENDER as Standard. Absent — every project
    // that predates the control — is the default.
    deflickerWindow: normalizeDeflickerWindow(s.deflickerWindow),
  };
};

// Transitions are on exactly when they contribute frames. Derived rather than
// stored so the badge on the card, the duration in the receipt and the enable
// layer written to Firestore can never drift apart.
export const transitionsEnabled = (draft) =>
  Number.isFinite(draft?.transitionFrames) && draft.transitionFrames > 0;

// --- the Motion section's either/or (#532) ---------------------------------
//
// MotionTrim (`trim_stillness`) DELETES still stretches; MotionPace
// (`variable_speed`) SPEEDS THROUGH them. The registry declares them an
// exclusive group, so at most one can ever run — the editor's segmented toggle
// is that rule made visible, and everything below DERIVES the toggle's state
// from `resolveOperations` rather than reimplementing the rule. A hand-written
// recipe that asks for both therefore shows the mode the RENDER will pick, not
// the one the UI would have guessed.

export const MOTION_MODE_TRIM = 'trim';
export const MOTION_MODE_PACE = 'pace';

// The feature-flag map `resolveOperations` wants, from the one flag this
// section cares about. Keyed by OPERATION ID, not by the flag's name — that is
// the shape `resolve_feature_flags` builds server-side (shared/operations.py),
// and the resolver looks up `flags[op.id]`.
export const motionFlags = (motionPaceEnabled) => ({
  [VARIABLE_SPEED]: !!motionPaceEnabled,
});

// The settings blob a draft's motion choice projects to. Only the two motion
// operations matter here, but the enable layer has to be built the same way
// `buildRenderSettings` builds it or the resolver would answer a different
// question than the render asks.
const motionSettingsForDraft = (draft) => {
  const d = draft || {};
  const tuning = { variableSpeed: !!d.variableSpeed };
  return {
    ...tuning,
    operations: buildOperationsBlock(tuning, {
      [TRIM_STILLNESS]: !!d.trimStillness,
      [VARIABLE_SPEED]: !!d.variableSpeed,
    }),
  };
};

/**
 * Which motion mode this draft RESOLVES to, or null when the section is off.
 *
 * `motionPaceEnabled` is the `motionPace` feature flag. While it is dark, a
 * draft asking for MotionPace resolves to MotionTrim exactly as the render
 * would. Two separate mechanisms deliver that, and both live in
 * `resolveOperations` rather than here: the flag gate runs before the exclusive
 * group, so a dark MotionPace never forces MotionTrim off; and the group's
 * `flagFallback` (#585) voids a `trim_stillness: false` that was only ever the
 * loser entry written beside a MotionPace win. Without the second one, every
 * project rendered on the MotionPace default opened fully OFF under a dark flag.
 */
export const motionModeOf = (draft, motionPaceEnabled) => {
  const resolved = resolveOperations(
    motionSettingsForDraft(draft), null, motionFlags(motionPaceEnabled),
  );
  if (resolved[VARIABLE_SPEED]) return MOTION_MODE_PACE;
  if (resolved[TRIM_STILLNESS]) return MOTION_MODE_TRIM;
  return null;
};

/** Whether the Motion section's switch reads as on. */
export const motionEnabled = (draft, motionPaceEnabled) =>
  motionModeOf(draft, motionPaceEnabled) !== null;

/**
 * The draft changes that select a mode (null switches the section off).
 *
 * BOTH booleans are always written, never just the one that changed. The
 * exclusive group would force the loser off at render time anyway, but a draft
 * that says "both on" and relies on that would persist an enable layer
 * describing an edit nobody chose — and `motionModeOf` above would then be the
 * only thing that knew which one actually runs.
 *
 * TURNING MOTIONPACE ON ALSO TURNS THE SPEED DIAL ON (#533). The dial is the
 * approved default for the feature, and this is the only place it can be
 * defaulted without the preview lying about an existing video: defaulting it in
 * `deriveEditDraft` instead would light the dial up for a project whose LAST
 * RENDER has none, and — because the persisted baseline is derived by that same
 * function — it would do so without raising the recompile CTA that is supposed
 * to flag exactly that divergence. Coming through a mode change makes it an
 * ordinary edit: it differs from the persisted recipe, so the CTA appears.
 */
export const motionModeChanges = (mode) => ({
  trimStillness: mode === MOTION_MODE_TRIM,
  variableSpeed: mode === MOTION_MODE_PACE,
  ...(mode === MOTION_MODE_PACE ? { showSpeedDial: true } : {}),
});

// --- the Motion section's DEFAULT mode -------------------------------------
//
// MotionPace is the mode a project opens on, not MotionTrim — but only for a
// project that has never said which it wants, and only while the `motionPace`
// flag is lit. Everything below is about keeping that a DEFAULT rather than a
// silent rewrite of somebody's saved edit.

// Whether this settings blob already carries a motion choice.
//
// Three channels can hold one, and any of them counts:
//
//   * `settings.operations.trim_stillness.enabled` — the only place "keep the
//     full length" can live, because trim_stillness is `enable: always` and has
//     no tuning key capable of expressing off.
//   * `settings.operations.variable_speed.enabled` — the same layer for
//     MotionPace, written by every settings save the Motion section makes.
//   * `settings.variableSpeed` — MotionPace's legacy tuning key, which an API
//     client (or an older render) may have written on its own.
//
// PRESENCE, NOT TRUTH: an explicit `false` is a choice too, and a default must
// not overrule it. A blob that names none of the three has never expressed an
// opinion, which is the only case the default applies to.
export const hasPersistedMotionChoice = (settings) => {
  const s = settings || {};
  if (s.variableSpeed !== undefined) return true;
  const block = s[OPERATIONS_KEY];
  if (!block || typeof block !== 'object') return false;
  return [TRIM_STILLNESS, VARIABLE_SPEED].some((id) => {
    const entry = block[id];
    return !!entry && typeof entry === 'object' && typeof entry.enabled === 'boolean';
  });
};

/**
 * Whether a draft seeded from these settings should open on MotionPace.
 *
 * THREE GUARDS, ALL REQUIRED:
 *
 *  1. `motionPaceEnabled` — the `motionPace` feature flag. While it is dark
 *     (production today) this is always false, so the default is exactly
 *     today's MotionTrim and nothing about the dark path changes. The flag is
 *     read where the draft is seeded (ProjectView) rather than assumed here.
 *  2. No persisted motion choice — see `hasPersistedMotionChoice`. A saved
 *     recipe always wins over a default.
 *  3. `hasRender` — a rendered video is on the page, or one is on its way.
 *     ProjectView derives this from `editedVideo.path` (written only by the
 *     completing transaction, never cleared) plus the two in-flight statuses,
 *     NOT from the mere presence of `editedVideo` — a project whose only
 *     render FAILED has no video to contradict and stays eligible for the
 *     default. Its recipe was resolved from settings that may carry no
 *     explicit enable layer at all: the render-time settings write is
 *     CONDITIONAL on
 *     `editDraftDiffersFromPersisted` (ProjectView.handleConfirmRender), and
 *     `generate_edited_video` only stamps the WATERMARK enable entry
 *     (timelapse/api/main.py), so a render made entirely on defaults leaves
 *     nothing behind that says "MotionTrim". Defaulting such a project to
 *     MotionPace would show a mode — and, through `motionModeChanges`, a speed
 *     dial — that the video sitting on the page does not have. So a project
 *     with a render keeps whatever its settings actually resolve to.
 */
export const defaultsToPace = (settings, { motionPaceEnabled, hasRender } = {}) => {
  if (!motionPaceEnabled) return false;
  if (hasRender) return false;
  return !hasPersistedMotionChoice(settings);
};

// HOW THE KILL SWITCH BEHAVES — read this before flipping `motionPace` dark.
//
// Re-seeding is NOT what rolls a rendered project back. The moment a project
// renders, `handleConfirmRender` persists the seeded draft (it differs from the
// stored blob, which is the whole point of seeding on top of `deriveEditDraft`),
// writing `variable_speed.enabled: true` AND `trim_stillness.enabled: false`.
// From then on `hasPersistedMotionChoice` is true, so `defaultsToPace` can never
// reach that project again — dark flag or lit.
//
// THE ROLLBACK HAPPENS ONE LAYER DOWN, in `resolveOperations` (#585). That blob
// used to resolve to NEITHER mode under a dark flag — the flag gate killed
// MotionPace, the persisted `false` killed MotionTrim, and the next render came
// out full length and untrimmed. The registry's `stillness` group now carries
// `flagFallback`, so a MotionPace request that loses to its feature flag voids
// the `trim_stillness: false` beside it and MotionTrim takes the edit.
//
// That is exact, not a guess: the `false` is only ever written as the
// exclusive-group loser next to a winner, so its presence beside a
// `variable_speed: true` identifies it. A genuine "Motion off" choice has
// `variable_speed` false or absent and is untouched.
//
// NOTHING IS REWRITTEN. The fallback is resolution-time only, so re-lighting the
// flag restores MotionPace exactly, with the user's ceiling and smoothness
// intact — and because the same shared resolver runs in the render worker, the
// editor and the render agree without a Firestore write. `seedEditDraft — the
// kill switch after a defaulted render` in editDraft.test.js pins all of it.

/**
 * Whether this project's source video was built by joining several clips —
 * i.e. whether the Clip joins row has anything to fade. Shared by
 * `defaultsToJoinFades` and ProjectView's seeding effect so the default and the
 * re-seed that delivers it can never disagree about what "combined" means.
 */
export const hasClipJoins = (video) =>
  normalizeGapFrames(video?.concatJoinFrames).length > 0;

/**
 * Whether a draft seeded from these settings should open with clip-join fades
 * ON — the default for a combined project that has never rendered. Fading at
 * every join is what somebody who combined several clips almost always wanted.
 *
 * THREE GUARDS, ALL REQUIRED:
 *
 *  1. No stored value. PRESENCE, NOT TRUTH — an explicit `false` is a choice,
 *     and a default must not overrule it.
 *  2. `hasRender` — a rendered video is on the page, or one is on its way. A
 *     project with one keeps the hard cuts that video was made with; only one
 *     with nothing on screen yet takes the new default. This is the SAME real
 *     marker `defaultsToPace` uses (ProjectView derives it from
 *     `editedVideo.path` plus the in-flight statuses), and it is real for a
 *     reason: NO KEY IN THE SETTINGS BLOB CAN STAND IN FOR IT. The analyzer
 *     merges its `default_settings` — `transition_length_frames` among them —
 *     into `project.settings` at the end of every analysis
 *     (motion_analysis.py), so a never-rendered project already carries the
 *     length; and a render only persists settings when the draft differs, so a
 *     COMPLETED render can leave a blob with neither transition key. Nothing
 *     stored separates "rendered before this feature" from "never rendered".
 *  3. The project actually HAS clip joins (`hasClipJoins`).
 *     `effectiveFadeFrames`'s `joinsApply` guard (mirrored by the worker)
 *     already makes the flag a no-op without them, but defaulting it on
 *     regardless would make every new single-file project's draft differ from
 *     its blob — forcing a settings write and a segment recalculation on its
 *     first render for a value that cannot change a pixel.
 *
 *     Joins are NOT a fact of the project at open time: the storage finalize
 *     trigger writes `video.concatJoinFrames` whenever a combined video lands,
 *     including on a RE-combine (someone adding a second clip to a project that
 *     analyzed as a single file). ProjectView's seeding effect therefore tracks
 *     them in `draftSeedRef` and re-seeds once when they first appear —
 *     otherwise this default would never reach the exact user it was written
 *     for without a page reload.
 */
export const defaultsToJoinFades = (settings, { hasRender, video } = {}) => {
  if (hasRender) return false;
  if ((settings || {}).clipJoinTransitions !== undefined) return false;
  return hasClipJoins(video);
};

/**
 * The draft a project OPENS on: `deriveEditDraft` plus the section's default
 * mode when the project has never chosen one, plus the clip-join fade default
 * when it has never rendered.
 *
 * WHY THIS IS A SEPARATE FUNCTION AND NOT A BRANCH INSIDE `deriveEditDraft`.
 * `deriveEditDraft` answers "what does the stored blob mean", and
 * `editDraftDiffersFromPersisted` re-derives the PERSISTED BASELINE with it. A
 * default applied in there would move both sides at once: the draft would say
 * MotionPace, the baseline would agree, the comparison would report "nothing
 * changed", and `handleConfirmRender` would skip the settings write — leaving
 * the worker to resolve the untouched blob and render a MotionTrim video under
 * a UI showing MotionPace. Applying the default ON TOP of the derivation keeps
 * the baseline honest, so the default reads as an ordinary unsaved edit: it
 * differs from the stored recipe, so the write happens and the render matches
 * the screen. Same argument the `motionModeChanges` docstring makes about the
 * speed dial — which is why the default is expressed AS a mode change and picks
 * up `showSpeedDial: true` from the one place that is allowed to set it.
 *
 * The clip-join default rides here for exactly the same reason: seeded on top,
 * a defaulted `true` differs from the stored blob, so the render-time settings
 * write actually happens and the worker fades the joins the ticked box promises.
 */
export const seedEditDraft = (settings, watermarkDefaults, options) => {
  const draft = deriveEditDraft(settings, watermarkDefaults);
  const seeded = defaultsToJoinFades(settings, options)
    ? { ...draft, clipJoinTransitions: true }
    : draft;
  if (!defaultsToPace(settings, options)) return seeded;
  return { ...seeded, ...motionModeChanges(MOTION_MODE_PACE) };
};

// The settings blob to persist for this draft.
//
// `removeWatermark` — and with it the four watermark customization keys — is
// deliberately NOT written here. They stay a per-render request carried on the
// generate_edited_video call, which writes both `settings.removeWatermark`
// (plus `settings.watermark*`) and the enable-layer entry itself
// (timelapse/api/main.py) — persisting a second, older copy from the client
// would race that write. Same reason `editDraftDiffersFromPersisted` ignores
// them: a watermark-only change never needs this settings write to reach the
// render.
// MotionPace's two tuning keys, or nothing.
//
// WRITTEN ONLY WHEN THEY MEAN SOMETHING: a project that has never used
// MotionPace keeps a settings blob that has never heard of it, rather than every
// render stamping two defaults into it. Once the blob DOES carry them (the user
// tried MotionPace, or an API client set them) they keep being written, so
// switching back to MotionTrim does not strand a stale ceiling for the next
// server-side curve computation to read.
//
// Values go through the same clamp-never-round resolvers the draft was hydrated
// with, so an untouched control writes back exactly the number it was given
// (#454: a slider's `step` must not quantize a stored value).
const motionPaceSettings = (persisted, draft) => {
  const p = persisted || {};
  const alreadyStored = p.maxSpeed !== undefined
    || p.smoothness !== undefined
    || p.showSpeedDial !== undefined;
  if (!draft.variableSpeed && !alreadyStored) return null;
  return {
    maxSpeed: resolveMaxSpeed(draft.maxSpeed),
    smoothness: resolveSmoothness(draft.smoothness),
    // A REAL boolean, always — the worker's `is True` gate treats anything else
    // as off, so writing the draft's raw value would let an undefined reach
    // Firestore and silently drop the dial the switch says is on.
    showSpeedDial: !!draft.showSpeedDial,
  };
};

/**
 * The settings blob to persist for a MOTION-MODE change alone (#532).
 *
 * WHY THIS IS NOT `buildRenderSettings`. MotionPace's speed curve is computed
 * server-side, by the same callable that persists settings, and read back off
 * the project document — so unlike every other card, the Motion section has to
 * write BEFORE the render or it has nothing to draw. This writes the motion keys
 * and nothing else: the transition length, the deflicker switch and the rest
 * stay a render-time concern exactly as they are today, so moving a speed slider
 * cannot quietly commit an unrelated half-made choice.
 *
 * The `operations` block is rebuilt because it has to be: the enable layer
 * outranks the tuning keys wherever the worker reads it, so a blob that set
 * `variableSpeed: true` next to a stale `variable_speed: {enabled: false}` from
 * an earlier render would compute no curve and silently render a trimmed video.
 */
export const buildMotionSettings = (persisted, draft) => {
  const tuning = {
    ...(persisted || {}),
    variableSpeed: !!draft.variableSpeed,
    ...(motionPaceSettings(persisted, draft) || {}),
  };
  return {
    ...tuning,
    operations: buildOperationsBlock(tuning, {
      [TRIM_STILLNESS]: !!draft.trimStillness,
      [VARIABLE_SPEED]: !!draft.variableSpeed,
    }),
  };
};

export const buildRenderSettings = (persisted, draft) => {
  const tuning = {
    ...(persisted || {}),
    transition_length_frames: draft.transitionFrames,
    // The enable key MotionPace's `enable: { mode: 'bool' }` rule reads. Written
    // like `deflicker` is — unconditionally, so switching MotionPace off reaches
    // the worker rather than leaving a stale `true` behind.
    variableSpeed: !!draft.variableSpeed,
    ...(motionPaceSettings(persisted, draft) || {}),
    deflicker: !!draft.deflicker,
    // Same posture as `deflicker`: a plain persisted key, not routed through
    // the registry's enable layer, so the worker's `settings.captureGapTransitions`
    // read always reflects the card's current switch state.
    captureGapTransitions: !!draft.captureGapTransitions,
    // Same posture again — the worker's `settings.clipJoinTransitions` read has
    // to reflect the card's current switch, including switching it back off.
    clipJoinTransitions: !!draft.clipJoinTransitions,
    // The Strength choice, read by the worker as `settings.deflickerWindow`
    // (video_editing.py::_deflicker_window). Written whatever the switch says:
    // the control is deliberately independent of it, so a user who picks Strong
    // on a card they left off has chosen how it will smooth when they turn it
    // on, and that choice must survive a reload. Normalised again rather than
    // trusted — this is the number the render runs.
    deflickerWindow: normalizeDeflickerWindow(draft.deflickerWindow),
  };
  return {
    ...tuning,
    // trim_stillness is `enable: always` in the registry, so the enable layer is
    // the only channel that can carry "keep the full length" to the worker.
    //
    // variable_speed rides along explicitly for a different reason: the two are
    // an exclusive group, and the group only ever forces a member OFF. Projecting
    // MotionTrim's switch alone would leave the block's MotionPace entry derived
    // from the tuning key, which is the same value — but stating both keeps the
    // persisted enable layer a complete description of the mode the user chose,
    // which is what the worker resolves first.
    operations: buildOperationsBlock(tuning, {
      [TRIM_STILLNESS]: !!draft.trimStillness,
      [VARIABLE_SPEED]: !!draft.variableSpeed,
    }),
  };
};

// Whether the draft still describes what is stored. Used to skip the settings
// write entirely on an unchanged re-render — and, more importantly, to make
// sure a trim_stillness flip is never skipped: the worker reads the enable
// layer off the project document, so an unpersisted "keep full length" would
// render a trimmed video.
//
// THE BASELINE IS `deriveEditDraft`, NEVER `seedEditDraft`. This has to answer
// "what does Firestore currently say", and the seed's MotionPace default is a
// choice made in the browser that Firestore has not heard yet. Seeding the
// baseline too would cancel the default out of the comparison and skip the very
// write that carries it to the worker — see `seedEditDraft`.
export const editDraftDiffersFromPersisted = (persisted, draft) => {
  if (!draft) return false;
  const current = deriveEditDraft(persisted);
  return (
    current.trimStillness !== !!draft.trimStillness
    // The mode flip has to reach Firestore for the same reason a trim flip does:
    // the worker resolves the enable layer off the project document, so an
    // unpersisted "pace this instead" would render a trimmed video.
    || current.variableSpeed !== !!draft.variableSpeed
    // Compared through the same clamp both sides were hydrated with, so a stored
    // 9x (out of range, clamped to 6 everywhere) is correctly NOT a change: the
    // render uses 6 either way, and the write would buy nothing.
    || current.maxSpeed !== resolveMaxSpeed(draft.maxSpeed)
    || current.smoothness !== resolveSmoothness(draft.smoothness)
    // The dial changes the rendered pixels, so it is a recipe change like any
    // other: this is what raises the recompile CTA (#526) when the switch no
    // longer matches the video that was last rendered.
    || current.showSpeedDial !== !!draft.showSpeedDial
    || current.deflicker !== !!draft.deflicker
    || current.transitionFrames !== draft.transitionFrames
    || current.captureGapTransitions !== !!draft.captureGapTransitions
    || current.clipJoinTransitions !== !!draft.clipJoinTransitions
    // Compared through the same snap as `current`, so a strength-only change
    // still triggers the write — without this the tile moved, the chart
    // redrew, and the render used the stored window. Both sides normalised
    // means a stored 12 against a drafted 11 is correctly NOT a change: the
    // worker renders both at 11, so the write would buy nothing.
    || current.deflickerWindow !== normalizeDeflickerWindow(draft.deflickerWindow)
  );
};

// The segments this edit will actually render.
//
// With stillness trimming ON these are the analyzed motion segments. With it
// OFF the edit covers the whole source, which the worker expresses as one
// full-length segment once ffprobe has read the file (video_editing.py). The
// client can only estimate that length from the stored duration × fps, and it
// is used for DISPLAY ONLY — the duration readout and the clip count that
// decides whether transitions can apply. Nothing here is sent to the worker.
export const effectiveSegments = (video, trimStillness) => {
  if (trimStillness) {
    return Array.isArray(video?.motionSegments) ? video.motionSegments : null;
  }
  const fps = Number(video?.fps);
  const length = Number(video?.length);
  if (!Number.isFinite(fps) || fps <= 0 || !Number.isFinite(length) || length <= 0) {
    return null;
  }
  const totalFrames = Math.round(length * fps);
  return totalFrames > 0 ? [{ start: 0, end: totalFrames }] : null;
};

// --- capture-gap splits ----------------------------------------------------
//
// The worker does not render `video.captureGapFrames` as a list of fades; it
// SPLITS the segment list at those frames and lets the existing xfade path do
// the rest (`_split_segments_at_capture_gaps`, processing/video_editing.py).
// Most gaps in a real project never become a fade — one landing on a segment
// boundary, inside stillness the trim removed, or too close to an edge is a
// no-op — and every gap that DOES split costs the output one transition's
// worth of frames. So a receipt that counts the raw array over-promises on
// both the crossfade count and the final duration.
//
// This is a frame-for-frame port of the worker's rules. Any change to either
// side needs the same change to the other.

// Mirrors MAX_CAPTURE_GAP_SPLITS (video_editing.py): render cost grows
// quadratically with the split count, so the worker applies at most this many
// and keeps every stride-th one to hold the fades spread across the shoot.
export const MAX_CAPTURE_GAP_SPLITS = 24;

// The segments the render will actually cut, with capture-gap splits applied.
//
// Returns `segments` untouched when nothing splits — including when
// `transitionFrames` is 0, because capture-gap fades ride on the transitions
// operation and the worker only reaches the splitter with a real fade length.
export const splitSegmentsAtCaptureGaps = (segments, gapFrames, transitionFrames) => {
  if (!Array.isArray(segments) || segments.length === 0) return segments;
  const fade = Number.isFinite(transitionFrames) ? Math.max(0, transitionFrames) : 0;
  if (fade <= 0) return segments;
  const frames = normalizeGapFrames(gapFrames);
  if (frames.length === 0) return segments;

  // A piece that cannot survive a head AND a tail crossfade contributes no
  // body at all, and its two fades would collide — so that cut is skipped.
  const minPiece = 2 * fade;

  const accepted = [];
  segments.forEach((seg, index) => {
    let start = seg?.start;
    const end = seg?.end;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;
    for (const frame of frames) {
      if (frame <= start || frame >= end) continue;
      if ((frame - start) < minPiece || (end - frame) < minPiece) continue;
      accepted.push([index, frame]);
      start = frame;
    }
  });
  if (accepted.length === 0) return segments;

  // Dropping a cut only merges two adjacent pieces, so every survivor is still
  // at least minPiece long.
  const stride = Math.ceil(accepted.length / MAX_CAPTURE_GAP_SPLITS);
  const kept = stride > 1 ? accepted.filter((_, i) => i % stride === 0) : accepted;

  const cuts = new Map();
  for (const [index, frame] of kept) {
    if (!cuts.has(index)) cuts.set(index, []);
    cuts.get(index).push(frame);
  }

  const split = [];
  segments.forEach((seg, index) => {
    let start = seg.start;
    for (const frame of cuts.get(index) || []) {
      split.push({ ...seg, start, end: frame });
      start = frame;
    }
    split.push({ ...seg, start, end: seg.end });
  });
  return split;
};

// Which clip boundaries the render will crossfade, one flag per boundary
// (`segments.length - 1` of them).
//
// Analysis cuts a segment at every capture gap, so on a multi-day project each
// day boundary arrives as its own segment boundary. The worker fades those
// only when the capture-gap switch is on, and at most MAX_CAPTURE_GAP_SPLITS
// of them; boundaries inside a single capture day always fade. Frame-for-frame
// port of `capture_gap_fade_boundaries` (processing/video_editing.py) — the
// receipt's crossfade count and duration are wrong the moment the two drift.
//
// A boundary counts as a gap boundary only when the two clips MEET at a gap
// frame — `segments[i].end === segments[i + 1].start` and that frame is a
// detected gap. Only a cut at a gap produces two touching segments (ordinary
// analysis would have merged anything that close), so a boundary with trimmed
// stillness between the clips is a normal edit cut that faded long before
// capture gaps existed and keeps fading.
//
// A gap boundary next to a segment shorter than `2 * transitionFrames` hard
// cuts even with the switch on — the same floor `splitSegmentsAtCaptureGaps`
// applies above. A day that short (an hourly camera can leave one 5 frames
// long) would lose its entire body to a head and a tail crossfade, and those
// fades would read past its own bounds into the neighbouring day.
//
// The reason-carrying core. `captureGapFadeBoundaries` below is the boolean view
// of it; `fadeBoundaryTally` needs the reason as well, because the two drops
// need two different sentences on the card (#589 follow-up): a 31-day shoot with
// 100-frame days loses fades to the CAP with every piece comfortably long
// enough, and "too short to overlap" would be flatly false there.
//
// Each entry is `{ fades, reason }`. `reason` is null when the boundary fades or
// was never a gap boundary, 'short' when a neighbouring piece cannot absorb the
// overlap, and 'capped' when the boundary was long enough but lost the
// MAX_CAPTURE_GAP_SPLITS stride.
const gapFadeDecisions = (
  segments, gapFrames, captureGapTransitions, transitionFrames,
) => {
  const count = Array.isArray(segments) ? segments.length : 0;
  const boundaries = Math.max(0, count - 1);
  if (boundaries === 0) return [];
  const frames = new Set(normalizeGapFrames(gapFrames));
  if (frames.size === 0) return new Array(boundaries).fill(null).map(() => ({ fades: true, reason: null }));

  const onGap = [];
  for (let i = 0; i < boundaries; i += 1) {
    const end = segments[i]?.end;
    const nextStart = segments[i + 1]?.start;
    onGap.push(Number.isFinite(end) && end === nextStart && frames.has(end));
  }
  // Switch off: gap boundaries are SUPPRESSED, not dropped for a reason the card
  // could explain, so they carry no reason.
  if (!captureGapTransitions) return onGap.map((gap) => ({ fades: !gap, reason: null }));

  // A segment that cannot survive a head AND a tail crossfade is not worth
  // fading into. Non-finite bounds fail the comparison and hard cut, which is
  // the conservative answer.
  const minPiece = 2 * (Number.isFinite(transitionFrames) ? Math.max(0, transitionFrames) : 0);
  const fits = (i) => (
    (segments[i].end - segments[i].start) >= minPiece
    && (segments[i + 1].end - segments[i + 1].start) >= minPiece
  );

  // Only boundaries that can actually fade compete for the cap.
  const gapIndices = [];
  onGap.forEach((gap, i) => { if (gap && fits(i)) gapIndices.push(i); });
  const stride = Math.ceil(gapIndices.length / MAX_CAPTURE_GAP_SPLITS);
  const kept = new Set(stride > 1 ? gapIndices.filter((_, i) => i % stride === 0) : gapIndices);
  return onGap.map((gap, i) => {
    if (!gap) return { fades: true, reason: null };
    if (kept.has(i)) return { fades: true, reason: null };
    // fits() decides which of the two rules dropped it: a boundary that does not
    // fit never entered the stride at all.
    return { fades: false, reason: fits(i) ? 'capped' : 'short' };
  });
};

export const captureGapFadeBoundaries = (
  segments, gapFrames, captureGapTransitions, transitionFrames,
) => gapFadeDecisions(segments, gapFrames, captureGapTransitions, transitionFrames)
  .map((d) => d.fades);

// How many crossfades the render will apply in total. Every boundary used to
// fade, so this was `segments.length - 1`; day-isolated segments made that an
// over-count, which inflated the transition overlap and under-reported the
// final duration.
//
// `transitionFrames` mirrors the worker's own guard: with no fade length there
// is nothing to suppress, and the pre-capture-gap arithmetic is kept exactly.
export const renderCrossfadeCount = (
  segments, gapFrames, captureGapTransitions, transitionFrames,
) => {
  const boundaries = Math.max(0, (Array.isArray(segments) ? segments.length : 0) - 1);
  const fade = Number.isFinite(transitionFrames) ? Math.max(0, transitionFrames) : 0;
  if (fade <= 0) return boundaries;
  return captureGapFadeBoundaries(segments, gapFrames, captureGapTransitions, fade)
    .filter(Boolean).length;
};

// How many crossfades the fade-boundary switches actually buy — the boundaries
// that fade because a switch is on, not the number of gaps or joins detected. A
// boundary inside trimmed stillness, one the render never reaches, or one past
// the MAX_CAPTURE_GAP_SPLITS cap never becomes a fade. Callers pass the union
// from `effectiveFadeFrames`, so this counts capture gaps and clip joins
// together — exactly as the worker renders them.
export const captureGapCrossfadeCount = (
  segments, gapFrames, captureGapTransitions, transitionFrames,
) => {
  if (!captureGapTransitions) return 0;
  const on = renderCrossfadeCount(segments, gapFrames, true, transitionFrames);
  const off = renderCrossfadeCount(segments, gapFrames, false, transitionFrames);
  return Math.max(0, on - off);
};

/**
 * Of the boundaries sitting on `countFrames`, how many the render will actually
 * dissolve (#589).
 *
 * WHY THE CARD NEEDS THIS. The render deliberately DROPS a fade when the pieces
 * on either side are too short to absorb the overlap, and again past the
 * `MAX_CAPTURE_GAP_SPLITS` cap. Both drops are correct — a day only 5 frames
 * long would lose its whole body to a head and a tail crossfade, and the fades
 * would read past its own bounds. But the Transitions card counted the
 * boundaries DETECTED, so it went on promising "3 gaps" worth of dissolves when
 * every one of them would be skipped.
 *
 * `fadeFrames` must be the union the render honours (`effectiveFadeFrames`), so
 * the cap is computed over the same set the worker caps over. `countFrames` is
 * the subset being reported on — capture gaps for one row, clip joins for the
 * other — which is what lets one helper answer both rows exactly.
 *
 * `boundaries` is NOT `countFrames.length`: a declared frame only becomes a
 * boundary when two rendered segments actually meet there, so a gap inside
 * trimmed stillness was never going to fade and is not counted as a loss. This
 * is why the card must quote `boundaries` as the shortfall's denominator rather
 * than the count of gaps DETECTED — the two differ, and subtracting one from the
 * other over-reports the losses.
 *
 * `droppedShort` + `droppedCapped` === `boundaries - fading`, and they split the
 * loss by CAUSE so the card can name the right one. `short` is a neighbouring
 * piece under the `2 * transitionFrames` floor; `capped` is a boundary that was
 * long enough and simply lost the MAX_CAPTURE_GAP_SPLITS stride — the ordinary
 * outcome on a multi-week shoot, where the "too short" sentence is untrue.
 */
export const fadeBoundaryTally = (
  segments, fadeFrames, transitionFrames, countFrames,
) => {
  const list = Array.isArray(segments) ? segments : [];
  const decisions = gapFadeDecisions(list, fadeFrames, true, transitionFrames);
  const counted = new Set(normalizeGapFrames(countFrames));
  let boundaries = 0;
  let fading = 0;
  let droppedShort = 0;
  let droppedCapped = 0;
  for (let i = 0; i < decisions.length; i += 1) {
    const end = list[i]?.end;
    if (Number.isFinite(end) && end === list[i + 1]?.start && counted.has(end)) {
      boundaries += 1;
      if (decisions[i].fades) fading += 1;
      else if (decisions[i].reason === 'capped') droppedCapped += 1;
      else droppedShort += 1;
    }
  }
  return { boundaries, fading, droppedShort, droppedCapped };
};

// The boundary frames this render will fade at, and whether fading is on.
//
// Frame-for-frame port of `effective_fade_frames`
// (processing/video_editing.py) — read that docstring for the full rule; the
// two asymmetries below are load-bearing and must change together:
//
//   * `captureGapFrames` is ALWAYS in `frames`, switch or no switch. With the
//     switch off those frames are what SUPPRESS the fade at the boundaries
//     analysis already cut at each gap; dropping them would hand every
//     existing multi-day edit the fades #433 exists to withhold.
//   * `concatJoinFrames` joins only when its switch is on, and only then does
//     it flip `enabled` — a photo-compile project has no joins at all, and
//     letting an irrelevant `clipJoinTransitions` flip the switch there would
//     un-suppress that project's capture-gap boundaries.
export const effectiveFadeFrames = (video, draft) => {
  const gaps = normalizeGapFrames(video?.captureGapFrames);
  const joins = normalizeGapFrames(video?.concatJoinFrames);
  const gapOn = !!draft?.captureGapTransitions;
  // `joinsApply` — not merely the switch. Also what the render dialog's
  // receipt reads to decide whether to call these boundaries clip joins.
  const joinsApply = !!draft?.clipJoinTransitions && joins.length > 0;
  const frames = joinsApply
    ? [...new Set([...gaps, ...joins])].sort((a, b) => a - b)
    : gaps;
  return { frames, enabled: gapOn || joinsApply, joinsApply };
};

// The segments this draft will actually render — `effectiveSegments` with the
// fade-boundary splits the worker will apply on top.
//
// `effectiveSegments` answers "what did analysis give us"; this answers "what
// will the worker cut", which is the only list the UI should ever count. The
// two differ exactly when a fade switch is on and a boundary qualifies to
// split, which is the case the feature exists for. Single definition so the
// Clips stat, the transitions card and the render dialog's receipt cannot
// disagree about the same edit.
export const renderSegmentsForDraft = (video, draft) => {
  const base = effectiveSegments(video, draft?.trimStillness);
  const { frames, enabled } = effectiveFadeFrames(video, draft);
  if (!enabled) return base;
  return splitSegmentsAtCaptureGaps(base, frames, draft?.transitionFrames ?? 0);
};

// The clip list a MOTIONPACE render produces, measured in OUTPUT frames — the
// pace-mode counterpart of `renderSegmentsForDraft` above, and the only honest
// input to that render's duration.
//
// WHY THE TRIM MATH DOES NOT DESCRIBE A PACE RENDER. Pacing rewrites the source
// before the clip chain ever sees it: the worker builds a paced video of
// `outputFrames` frames at `outputFps` and hands the pipeline ONE full-length
// segment counted in those frames (`video_editing.py`, the VARIABLE_SPEED
// branch). Segments measured in source frames therefore describe a video that
// is not being rendered.
//
// BUT A PACE RENDER STILL PAYS FOR ITS CROSSFADES. The worker remaps the fade
// boundaries into that same output space and splits the paced clip at them, so
// each surviving join costs `transitionFrames` of overlap exactly as it does on
// a trim render. Quoting the curve's raw length overstated a multi-day paced
// render by `crossfades × transitionFrames / fps` seconds — while the receipt
// listed those very crossfades on the next row.
//
// So the whole plan is rebuilt in output space and then run through the SAME
// splitter, the SAME boundary rules and the SAME duration helper the trim path
// uses. Only the units and the marker positions change.
//
// The counts cannot simply be reused from the trim path even though pacing
// forces stillness trimming off (the registry's exclusive group): a day that is
// comfortably longer than `2 * transitionFrames` in source frames can be far
// shorter than that once the walk has sprinted through it, and the worker hard
// cuts there. Same rule, applied to the frames the render actually has.
//
// Returns null when the curve cannot answer — no curve stored (#573), or a
// record too damaged to measure — which is the caller's signal to fall back to
// the trim segment math rather than state a confident 0:00:00.
export const pacedRenderPlan = (video, draft) => {
  const speedCurve = video?.[SPEED_CURVE_FIELD];
  const span = paceOutputSpan(speedCurve);
  if (!span) return null;

  const transitionFrames = draft?.transitionFrames ?? 0;
  // Reconciled from both fade declarations BEFORE the remap, which is the order
  // the worker uses too: one already-normalized list is moved into output space,
  // so a clip join and a capture gap are moved by exactly the same rule.
  const { frames, enabled } = effectiveFadeFrames(video, draft);
  const gapFrames = enabled
    ? remapGapFramesToOutput(frames, speedCurve.frameIndices)
    : [];

  const whole = [{ start: 0, end: span.outputFrames }];
  const segments = enabled
    ? splitSegmentsAtCaptureGaps(whole, gapFrames, transitionFrames)
    : whole;

  return {
    segments,
    outputFps: span.outputFps,
    crossfadeCount: renderCrossfadeCount(segments, gapFrames, enabled, transitionFrames),
    captureGapCount: captureGapCrossfadeCount(segments, gapFrames, enabled, transitionFrames),
  };
};

// How many clips the render will join. Transitions are an xfade BETWEEN clips
// (registry: `filter.scope === 'betweenClips'`), so a single clip cannot show
// one no matter what the preset says — the card says so rather than letting the
// user pick a fade that silently does nothing.
//
// Takes the whole draft rather than `trimStillness` alone: counting the unsplit
// list told a one-segment compile with overnight gaps that its crossfade had
// "no effect" in precisely the case it does the most work.
export const clipCount = (video, draft) => {
  const segments = renderSegmentsForDraft(video, draft);
  return Array.isArray(segments) ? segments.length : null;
};

// `video.status` values that mean the source is still landing — an upload on
// its way through motion analysis. The client's copy of
// `_IN_FLIGHT_VIDEO_STATUSES` in packages/functions/timelapse/api/main.py.
// Anything else ('analyzed', 'failed', 'cancelled') is terminal: analysis will
// not write anything more without a new request.
export const IN_FLIGHT_VIDEO_STATUSES = ['queued', 'uploading', 'analyzing'];

export const isAnalysisInFlight = (video) =>
  IN_FLIGHT_VIDEO_STATUSES.includes(video?.status);

// `editedVideo.status` values that mean a render is still running. The client's
// copy of `_IN_FLIGHT_RENDER_STATUSES` in
// packages/functions/timelapse/api/main.py. The complete set written anywhere is
// queued/processing/complete/failed/cancelled, so the other three are terminal:
// no further worker write lands without a new request.
export const IN_FLIGHT_RENDER_STATUSES = ['queued', 'processing'];

export const isRenderInFlight = (editedVideo) =>
  IN_FLIGHT_RENDER_STATUSES.includes(editedVideo?.status);

// `photoCompile.status` values that mean a photos-to-video compile is still
// running. The client's copy of `_IN_FLIGHT_COMPILE_STATUSES` in
// packages/functions/timelapse/api/main.py.
export const IN_FLIGHT_COMPILE_STATUSES = ['queued', 'processing'];

// COMPILE_STALE_AFTER_SECONDS (main.py), in milliseconds. The compile worker
// heartbeats `photoCompile.updatedAt` every couple of seconds, so this much
// silence means the worker died (a SIGKILLed run never writes 'failed') or the
// pointer swap never landed. The server drops BOTH compile-side gates at that
// point, because otherwise the project could never be rendered again — so the
// client has to drop them too, or the button would stay greyed out over a
// render the server would happily accept.
export const COMPILE_STALE_AFTER_MS = 15 * 60 * 1000;

// `photoCompile.updatedAt` as epoch milliseconds, or null when it cannot be
// read. Firestore hands the web SDK a Timestamp, but the same field may arrive
// as a plain Date (optimistic local writes) or as a `{seconds}` shape, so every
// form is accepted and anything unrecognised reads as "no liveness signal".
const compileUpdatedAtMs = (updatedAt) => {
  if (!updatedAt) return null;
  try {
    if (typeof updatedAt.toMillis === 'function') {
      const ms = updatedAt.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof updatedAt.toDate === 'function') {
      const ms = updatedAt.toDate()?.getTime();
      return Number.isFinite(ms) ? ms : null;
    }
  } catch (e) {
    return null;
  }
  if (updatedAt instanceof Date) {
    const ms = updatedAt.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (Number.isFinite(updatedAt.seconds)) return updatedAt.seconds * 1000;
  return null;
};

// Whether `photoCompile` looks abandoned rather than live — the client's copy of
// `_compile_is_stale` (main.py). A missing or unreadable `updatedAt` counts as
// stale, matching the server's deliberate fail-OPEN: not every writer maintained
// the field, and guessing "live" would wedge the render button forever.
export const compileIsStale = (photoCompile, nowMs = Date.now()) => {
  const updatedAtMs = compileUpdatedAtMs(photoCompile?.updatedAt);
  if (updatedAtMs === null) return true;
  return nowMs - updatedAtMs >= COMPILE_STALE_AFTER_MS;
};

// The epoch-ms instant `photoCompile` crosses into stale, or null when there is
// no instant to wait for (no readable `updatedAt`, which already reads as
// stale).
//
// Exists because the expiry above is the only time-based gate in this file, and
// the UI has no clock: the panel re-renders on Firestore snapshots, and the
// case the expiry exists for is a SIGKILLed worker that writes nothing. So the
// moment the gate flips produces no re-render. A caller schedules one against
// this deadline — see `EditorPanel`.
export const compileStaleDeadlineMs = (photoCompile) => {
  const updatedAtMs = compileUpdatedAtMs(photoCompile?.updatedAt);
  if (updatedAtMs === null) return null;
  return updatedAtMs + COMPILE_STALE_AFTER_MS;
};

// Whether a photos-to-video compile is holding the source slot, which is the
// other two thirds of the server's SOURCE_NOT_READY refusal (main.py, in
// `generate_edited_video`). Two windows:
//
//  * COMPILING — `photoCompile.status` is queued/processing. The mp4 the render
//    would be priced and cut from does not exist yet.
//  * SWAP PENDING — the compile finished and announced its upload in
//    `pendingOutputPath`, but the async analyze trigger has not re-pointed
//    `video.storagePath` at it yet (seconds to minutes). Gated on
//    status === 'complete' exactly as the server is, so a compile that FAILED
//    after announcing its output does not block renders forever.
//
// Both windows are invisible to `video.status`: it still reads the OLD video's
// 'analyzed' throughout, which is why this cannot be folded into
// `isAnalysisInFlight`. Both are dropped once the compile goes stale, mirroring
// the server's expiry.
export const isCompileBlocking = (video, photoCompile, nowMs = Date.now()) => {
  const compile = photoCompile || {};
  const inFlight = IN_FLIGHT_COMPILE_STATUSES.includes(compile.status);
  const pendingOutput = compile.pendingOutputPath;
  const swapPending = Boolean(
    compile.status === 'complete'
    && pendingOutput
    && pendingOutput !== video?.storagePath
  );
  if (!inFlight && !swapPending) return false;
  return !compileIsStale(compile, nowMs);
};

// Why the render button is unavailable, or null when it is available.
//
// Takes the whole PROJECT, not just its video: two of the three server
// preconditions below live on `project.photoCompile`.
//
// This is the client's mirror of the server-side preconditions on a render:
//
//  1. `generate_edited_video` (the callable that charges credits and queues the
//     worker) refuses any project whose `video.status` is still in flight, with
//     FAILED_PRECONDITION / SOURCE_NOT_READY — credits are charged from
//     `video.length`, which analysis has not written yet, so the render would be
//     priced at 0 (#381). No switch on this page can lift that, so the block
//     must stay up for the whole analysis window and its copy must not promise
//     an escape that ends in a generic error alert.
//  2. The SAME SOURCE_NOT_READY refusal also covers a photos-to-video compile
//     that is still running or whose output has not been swapped in yet — see
//     `isCompileBlocking`. Nothing else in the Edits UI reads `photoCompile`, so
//     without this a re-compile left the render button live: the click persisted
//     the settings flip through `recalculate_motion_segments` and only THEN hit
//     the generic "An error occurred while creating the video" alert.
//  3. `_claim_project_for_processing` (video_editing.py) needs motion segments
//     only when an enabled operation consumes them — so once analysis is
//     TERMINAL, switching stillness trimming off really is the way forward for
//     a project analysis found nothing in. That zero-segment dead end is the
//     case this feature exists for.
//
// Returning a REASON rather than a boolean is the point of #401 — the old gate
// disabled the button and named no way out.
export const renderBlockReason = (project, draft) => {
  const video = project?.video;
  // Checked before `no_source` so a FIRST compile — which has no video at all
  // yet — says "your photos are being compiled" instead of "waiting for your
  // upload". Both block; only the copy differs.
  if (isCompileBlocking(video, project?.photoCompile)) return 'compile_pending';
  if (!video?.storagePath) return 'no_source';
  if (isAnalysisInFlight(video)) return 'analysis_pending';
  if (!draft?.trimStillness) return null;
  const segments = video.motionSegments;
  // Terminal, but analysis never wrote a segment list (a failed or cancelled
  // run). Distinct from `no_segments` so the copy doesn't claim the footage was
  // examined and found still.
  if (!Array.isArray(segments)) return 'analysis_unavailable';
  if (segments.length === 0) return 'no_segments';
  return null;
};
